const TEXT_FIT_MIN_FONT_SIZE = 7;
const RANGE_FIELD_TYPES = new Set(['date', 'time']);
const AUTO_FONT_SIZE_MODES = new Set(['manual', 'auto_fit']);
const WRAP_MODES = new Set(['same_line', 'manual_lines', 'same_word', 'new_words']);
const RANGE_MODES = new Set(['single', 'range']);
const DEFAULT_LINE_HEIGHT = 1.15;

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function normalizeValuesJson(value) {
    if (!value) return {};
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                return parsed;
            }
            if (typeof parsed === 'string') {
                try {
                    const nested = JSON.parse(parsed);
                    return nested && typeof nested === 'object' ? nested : {};
                } catch {
                    return {};
                }
            }
            return {};
        } catch {
            return {};
        }
    }
    if (typeof value === 'object') {
        return value;
    }
    return {};
}

function normalizeCheckboxValue(value) {
    if (value === 'checked' || value === 'cross' || value === '') {
        return value;
    }
    if (value === true || value === 'true') {
        return 'checked';
    }
    if (value === false || value === 'false' || value === null || value === undefined) {
        return '';
    }
    return '';
}

function getCheckboxDisplayValue(value) {
    const normalized = normalizeCheckboxValue(value);
    if (normalized === 'checked') return '✓';
    if (normalized === 'cross') return 'X';
    return '';
}

function getNextCheckboxValue(value) {
    const normalized = normalizeCheckboxValue(value);
    if (normalized === 'checked') return 'cross';
    if (normalized === 'cross') return '';
    return 'checked';
}

function containsHebrew(value) {
    return /[\u0590-\u05ff]/.test(value || '');
}

function normalizeAlignValue(value) {
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (normalized === 'left' || normalized === 'center' || normalized === 'right') {
        return normalized;
    }
    return '';
}

function normalizeDirectionValue(value) {
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (normalized === 'rtl' || normalized === 'ltr') {
        return normalized;
    }
    return '';
}

function normalizeFontSizeMode(value) {
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return AUTO_FONT_SIZE_MODES.has(normalized) ? normalized : 'auto_fit';
}

function normalizeWrapMode(value) {
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return WRAP_MODES.has(normalized) ? normalized : 'same_word';
}

function normalizeRangeMode(value) {
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return RANGE_MODES.has(normalized) ? normalized : 'single';
}

function isRangeField(field) {
    return RANGE_FIELD_TYPES.has(field?.type) && normalizeRangeMode(field?.rangeMode) === 'range';
}

function normalizeRangeValue(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return {
            from: value.from === null || value.from === undefined ? '' : String(value.from),
            to: value.to === null || value.to === undefined ? '' : String(value.to),
        };
    }
    if (value === null || value === undefined || value === '') {
        return { from: '', to: '' };
    }
    return { from: String(value), to: '' };
}

function normalizeStoredFieldValue(field, value) {
    if (field?.type === 'checkbox') {
        return normalizeCheckboxValue(value);
    }
    if (isRangeField(field)) {
        return normalizeRangeValue(value);
    }
    return value;
}

function resolveFieldValue(field, values) {
    if (!field) return '';
    const rawValue = normalizeStoredFieldValue(field, values?.[field.id]);
    if (field.type === 'text' && field.isReadOnly) {
        if (!isFieldValueEmpty(field, rawValue)) {
            return rawValue;
        }
        return field.staticValue || '';
    }
    return rawValue;
}

function isFieldValueEmpty(field, value) {
    if (field?.type === 'checkbox') {
        return getCheckboxDisplayValue(value) === '';
    }
    if (field?.type === 'signature') {
        return !value;
    }
    if (isRangeField(field)) {
        const normalized = normalizeRangeValue(value);
        return !normalized.from && !normalized.to;
    }
    return value === null || value === undefined || value === '';
}

function formatSingleValue(field, value, formatDateFn) {
    if (value === null || value === undefined || value === '') return '';
    if (field?.type === 'checkbox') {
        return getCheckboxDisplayValue(value);
    }
    if (field?.type === 'date') {
        const formatted = typeof formatDateFn === 'function' ? formatDateFn(value) : '';
        return formatted || String(value);
    }
    return String(value);
}

function formatRangeDisplay(fromValue, toValue) {
    if (!fromValue && !toValue) return '';
    if (!toValue) return fromValue;
    if (!fromValue) return toValue;
    return `${fromValue} - ${toValue}`;
}

function getValueDisplay(field, value, { formatDateFn } = {}) {
    if (isRangeField(field)) {
        const normalized = normalizeRangeValue(value);
        const fromDisplay = formatSingleValue(field, normalized.from, formatDateFn);
        const toDisplay = formatSingleValue(field, normalized.to, formatDateFn);
        return formatRangeDisplay(fromDisplay, toDisplay);
    }
    return formatSingleValue(field, value, formatDateFn);
}

function resolveDirection(field, value) {
    const explicit = normalizeDirectionValue(field?.direction);
    if (explicit) {
        return explicit;
    }
    return containsHebrew(String(value || '')) ? 'rtl' : 'ltr';
}

function resolveAlign(field, direction) {
    const explicit = normalizeAlignValue(field?.align);
    if (explicit) return explicit;
    return direction === 'rtl' ? 'right' : 'left';
}

function resolveJustifyContent(align, direction) {
    if (align === 'center') return 'center';
    if (align === 'right') {
        return direction === 'rtl' ? 'flex-start' : 'flex-end';
    }
    if (align === 'left') {
        return direction === 'rtl' ? 'flex-end' : 'flex-start';
    }
    return 'flex-start';
}

function getWrapTextStyles(field) {
    const wrapMode = normalizeWrapMode(field?.wrapMode);
    if (wrapMode === 'same_line') {
        return {
            whiteSpace: 'nowrap',
            overflowWrap: 'normal',
            wordBreak: 'normal',
        };
    }
    if (wrapMode === 'manual_lines') {
        return {
            whiteSpace: 'pre-line',
            overflowWrap: 'normal',
            wordBreak: 'normal',
        };
    }
    if (wrapMode === 'new_words') {
        return {
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
        };
    }
    return {
        whiteSpace: 'pre-wrap',
        overflowWrap: 'normal',
        wordBreak: 'normal',
    };
}

function fitsText(wrapper, textNode) {
    const width = wrapper.clientWidth || wrapper.offsetWidth;
    const height = wrapper.clientHeight || wrapper.offsetHeight;
    if (!width || !height) return true;
    return (
        textNode.scrollWidth <= width + 0.5 &&
        textNode.scrollHeight <= height + 0.5
    );
}

function fitTextNode(wrapper, textNode, { baseFontSize, fontScale = 1 } = {}) {
    const numericBase = Number(baseFontSize);
    if (!Number.isFinite(numericBase) || numericBase <= 0) {
        return null;
    }
    const minFontSize = TEXT_FIT_MIN_FONT_SIZE * Math.max(fontScale, 1);
    const step = Math.max(0.25, Math.min(1, fontScale * 0.35));
    let nextSize = numericBase;
    textNode.style.fontSize = `${numericBase}px`;
    while (nextSize > minFontSize && !fitsText(wrapper, textNode)) {
        nextSize = clamp(nextSize - step, minFontSize, numericBase);
        textNode.style.fontSize = `${nextSize}px`;
    }
    return nextSize;
}

function applyFieldTextLayout({
    wrapper,
    textNode,
    field,
    value,
    baseFontSize,
    fontScale = 1,
    color = '#111827',
    lineHeight = DEFAULT_LINE_HEIGHT,
    formatDateFn,
}) {
    if (!wrapper || !textNode || !field) {
        return {
            displayText: '',
            align: 'left',
            direction: 'ltr',
            justifyContent: 'flex-start',
        };
    }
    const displayText = getValueDisplay(field, value, { formatDateFn });
    const direction = resolveDirection(field, displayText);
    const align = resolveAlign(field, direction);
    const justifyContent = resolveJustifyContent(align, direction);
    const wrapStyles = getWrapTextStyles(field);
    const isRange = isRangeField(field);

    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = justifyContent;
    wrapper.style.padding = '2px 4px';
    wrapper.style.overflow = 'hidden';
    wrapper.style.boxSizing = 'border-box';
    wrapper.style.direction = direction;
    wrapper.style.textAlign = align;
    wrapper.style.fontFamily = 'NotoSansHebrew, Assistant, sans-serif';

    textNode.textContent = displayText;
    textNode.style.display = 'block';
    textNode.style.width = '100%';
    textNode.style.color = color;
    textNode.style.fontFamily = 'NotoSansHebrew, Assistant, sans-serif';
    textNode.style.textAlign = align;
    textNode.style.direction = isRange ? 'ltr' : direction;
    textNode.style.unicodeBidi = isRange ? 'isolate' : 'plaintext';
    textNode.style.lineHeight = String(lineHeight);
    textNode.style.fontSize = `${baseFontSize}px`;
    textNode.style.whiteSpace = wrapStyles.whiteSpace;
    textNode.style.overflowWrap = wrapStyles.overflowWrap;
    textNode.style.wordBreak = wrapStyles.wordBreak;

    if (normalizeFontSizeMode(field?.fontSizeMode) === 'auto_fit' && displayText) {
        fitTextNode(wrapper, textNode, { baseFontSize, fontScale });
    }

    return { displayText, align, direction, justifyContent };
}

export {
    TEXT_FIT_MIN_FONT_SIZE,
    clamp,
    normalizeValuesJson,
    normalizeCheckboxValue,
    getCheckboxDisplayValue,
    getNextCheckboxValue,
    containsHebrew,
    normalizeAlignValue,
    normalizeDirectionValue,
    normalizeFontSizeMode,
    normalizeWrapMode,
    normalizeRangeMode,
    isRangeField,
    normalizeRangeValue,
    normalizeStoredFieldValue,
    resolveFieldValue,
    isFieldValueEmpty,
    formatRangeDisplay,
    getValueDisplay,
    resolveDirection,
    resolveAlign,
    resolveJustifyContent,
    getWrapTextStyles,
    fitTextNode,
    applyFieldTextLayout,
};
